<template>
    <LoginHeader>
        <p class="text-center mb-10 text-2xl font-bold">Forgot Password</p>

        <div class="login-form">
            <div class="left-side text-left">
                <form @submit.prevent="handleSubmit">
                    <!-- Step 1: Enter Username -->
                    <div class="form-group" v-if="step === 1">
                        <label for="username">Enter Username:</label>
                        <input type="text" id="username" v-model="username" placeholder="Enter username" required />
                        <button type="button" class="bg-primary rounded mt-4" @click="sendOtp"
                            :disabled="loadingButton === 'sendOtp'">
                            <span v-if="loadingButton === 'sendOtp'">Sending OTP...</span>
                            <span v-else>Send OTP</span>
                        </button>
                    </div>
                    <router-link v-if="step === 1" to="/" class="text-center mt-4 text-sm text-blue-600 cursor-pointer hover:underline">
                        Back to Login
                    </router-link>

                    <!-- Step 2: Enter OTP -->
                    <div class="form-group" v-if="step === 2">
                        <label for="otp">Enter OTP:</label>
                        <OtpInput :otpLength="6" @updateOtp="otp = $event" />
                        <p class="text-right mt-4 text-sm text-blue-600 cursor-pointer hover:underline"
                            @click="resendOtp"
                            :class="{ 'opacity-50 cursor-not-allowed': loadingButton === 'resendOtp' }">
                            <span v-if="loadingButton === 'resendOtp'">Resending...</span>
                            <span v-else>Resend OTP</span>
                        </p>
                        <button type="button" class="bg-primary rounded mt-4" @click="validateOtp"
                            :disabled="loadingButton === 'validateOtp'">
                            <span v-if="loadingButton === 'validateOtp'">Validating...</span>
                            <span v-else>Validate OTP</span>
                        </button>
                    </div>

                    <!-- Step 3: Enter New Password -->
                    <div class="form-group" v-if="step === 3">
                        <label for="newPassword">Create New Password:</label>
                        <input type="password" id="newPassword" v-model="newPassword" placeholder="Enter new password"
                            required @input="checkPasswordStrength" />

                        <!-- Password Strength Meter -->
                        <div class="password-strength">
                            <span :style="{ width: strengthPercentage + '%', backgroundColor: strengthColor }"></span>
                        </div>
                        <p class="text-sm mt-1 text-gray-500">{{ strengthMessage }}</p>

                        <button type="submit" class="bg-primary rounded mt-4"
                            :disabled="loadingButton === 'resetPassword'">
                            <span v-if="loadingButton === 'resetPassword'">Submitting...</span>
                            <span v-else>Submit</span>
                        </button>
                    </div>


                    <p v-if="errorMessages" class="text-red-500 mt-2">{{ errorMessages }}</p>
                    <p v-if="successMessage" class="text-green-500 mt-2">{{ successMessage }}</p>
                </form>
            </div>
        </div>
    </LoginHeader>
</template>

<script>
import LoginHeader from "@/components/LoginHeader.vue";
import OtpInput from "@/components/OtpInput.vue";
import axios from "axios";

export default {
    name: 'ForgetPassword',
    components: { LoginHeader, OtpInput },
    data() {
        return {
            step: 1, // Steps: 1 - Username, 2 - OTP, 3 - New Password
            username: '',
            otp: '',
            newPassword: '',
            loadingButton: null,
            errorMessages: '',
            successMessage: '',
            strengthPercentage: 0,
            strengthColor: 'red',
            strengthMessage: ''
        };
    },
    methods: {
        checkPasswordStrength() {
            const password = this.newPassword;
            let strength = 0;

            if (password.length >= 8) strength++; // Minimum length
            if (/[A-Z]/.test(password)) strength++; // Uppercase
            if (/[a-z]/.test(password)) strength++; // Lowercase
            if (/\d/.test(password)) strength++; // Numbers
            if (/[\W_]/.test(password)) strength++; // Special characters

            this.strengthPercentage = (strength / 5) * 100;
            switch (strength) {
                case 0:
                case 1:
                    this.strengthMessage = "Too Weak";
                    this.strengthColor = "red";
                    break;
                case 2:
                    this.strengthMessage = "Weak";
                    this.strengthColor = "orange";
                    break;
                case 3:
                    this.strengthMessage = "Moderate";
                    this.strengthColor = "yellow";
                    break;
                case 4:
                    this.strengthMessage = "Strong";
                    this.strengthColor = "green";
                    break;
                case 5:
                    this.strengthMessage = "Very Strong";
                    this.strengthColor = "darkgreen";
                    break;
            }
        },
        async sendOtp() {
            if (this.loadingButton) return;
            this.loadingButton = 'sendOtp';
            this.errorMessages = '';

            try {
                const response = await axios.post('https://net-banking.uat-api.dmcbank.co.in/forget-password', { username: this.username });

                if (response.data.status === 'success') {
                    this.successMessage = 'OTP sent successfully!';
                    this.step = 2; // Move to OTP step
                    setTimeout(() => { this.successMessage = ''; }, 3000);
                } else {
                    this.showError(response.data.message || 'Failed to send OTP');
                }
            } catch (error) {
                this.showError('Error sending OTP: ' + (error.message || 'Unknown error'));
            } finally {
                this.loadingButton = null;
            }
        },

        async resendOtp() {
            if (this.loadingButton) return;
            this.loadingButton = 'resendOtp';

            try {
                const response = await axios.post('https://net-banking.uat-api.dmcbank.co.in/resend-otp', {
                    username: this.username,
                    context: 'login'
                });

                if (response.data.status === 'success') {
                    this.successMessage = 'OTP resent successfully!';
                    setTimeout(() => { this.successMessage = ''; }, 3000);
                } else {
                    this.showError(response.data.message || 'Failed to resend OTP');
                }
            } catch (error) {
                this.showError('Error resending OTP: ' + (error.message || 'Unknown error'));
            } finally {
                this.loadingButton = null;
            }
        },

        async validateOtp() {
            if (this.loadingButton) return;
            this.loadingButton = 'validateOtp';

            if (this.otp.length !== 6) {
                this.showError('Enter a valid 6-digit OTP');
                this.loadingButton = null;
                return;
            }

            try {
                const response = await axios.post('https://net-banking.uat-api.dmcbank.co.in/forgetpass-verifyotp', {
                    username: this.username,
                    otp: this.otp
                });

                if (response.data.status === 'success') {
                    this.successMessage = 'OTP validated successfully!';
                    this.step = 3;
                    setTimeout(() => { this.successMessage = ''; }, 3000);
                } else {
                    this.showError(response.data.message || 'Failed to validate OTP');
                }
            } catch (error) {
                this.showError('Error validating OTP: ' + (error.message || 'Unknown error'));
            } finally {
                this.loadingButton = null;
            }
        },

        async handleSubmit() {
            if (this.loadingButton) return;
            this.loadingButton = 'resetPassword';

            if (this.strengthPercentage < 60) {
                this.showError('Password strength is too weak. Please create a stronger password.');
                this.loadingButton = null;
                return;
            }

            try {
                const response = await axios.post('https://net-banking.uat-api.dmcbank.co.in/create-newpass', {
                    username: this.username,
                    password: this.newPassword
                });

                if (response.data.status === 'success') {
                    this.successMessage = 'Password reset successfully! Redirecting...';
                    setTimeout(() => this.$router.push('/'), 2000);
                } else {
                    this.showError(response.data.message || 'Failed to reset password');
                }
            } catch (error) {
                this.showError(error.response?.data?.message || 'Error resetting password: ' + (error.message || 'Unknown error'));
            } finally {
                this.loadingButton = null;
            }
        },
        showError(message) {
            this.errorMessages = message;
            if (this.errorTimeout) {
                clearTimeout(this.errorTimeout);
            }
            this.errorTimeout = setTimeout(() => {
                this.errorMessages = '';
            }, 3000);
        },
    }
};
</script>

<style scoped>
.login-form {
    display: flex;
    justify-content: center;
}

.left-side {
    width: 50%;
}

.form-group {
    margin-bottom: 15px;
}

.form-group label {
    display: block;
}

.form-group input {
    width: 100%;
    padding: 8px;
    margin-top: 5px;
    border: 1px solid #ccc;
    border-radius: 4px;
}

button {
    width: 100%;
    padding: 10px;
    color: white;
    border: none;
    cursor: pointer;
}

.divider {
    width: 1px;
    background-color: #ddd;
    margin: 0 20px;
}

.password-strength {
    height: 5px;
    background-color: #ddd;
    border-radius: 4px;
    margin-top: 5px;
    overflow: hidden;
}

.password-strength span {
    display: block;
    height: 100%;
    transition: width 0.3s ease-in-out, background-color 0.3s ease-in-out;
}
</style>
